import React, { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";

const categories = ["General", "Test Series", "Editorial", "Study Plan", "Other"];

const initialForm = {
  title: "",
  body: "",
  category: "General",
  isImportant: false,
  visibility: {
    startDate: "",
    endDate: "",
  },
};

const API_BASE = process.env.REACT_APP_API_URL || "https://upsc-backend-353e.onrender.com";

const AnnouncementManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [filteredCategory, setFilteredCategory] = useState("All");
  const [form, setForm] = useState(initialForm);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const token = JSON.parse(localStorage.getItem("user"))?.token;

  const fetchAnnouncements = async () => {
  try {
    const res = await axios.get(`${API_BASE}/api/admin/announcements`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // Handle both possible backend response shapes
    const data = Array.isArray(res.data)
      ? res.data
      : Array.isArray(res.data?.announcements)
      ? res.data.announcements
      : [];

    setAnnouncements(data);
    console.log("✅ Announcements fetched:", data.length);
  } catch (err) {
    console.error("❌ Failed to fetch announcements:", err.response?.data || err.message);
    setAnnouncements([]);
  }
};

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "startDate" || name === "endDate") {
      setForm((prev) => ({
        ...prev,
        visibility: { ...prev.visibility, [name]: value },
      }));
    } else if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  const config = { headers: { Authorization: `Bearer ${token}` } };

  // ✅ Validate required fields on frontend too
  if (
    !form.title.trim() ||
    !form.body.trim() ||
    !form.visibility.startDate ||
    !form.visibility.endDate
  ) {
    alert("Please fill all required fields before saving.");
    setLoading(false);
    return;
  }

  // Convert to full ISO date format (00:00 - 23:59) to avoid UTC parsing bugs
const start = new Date(`${form.visibility.startDate}T00:00:00`);
const end = new Date(`${form.visibility.endDate}T23:59:59`);

const payload = {
  title: form.title.trim(),
  body: form.body.trim(),
  category: form.category,
  isImportant: form.isImportant,
  visibility: {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  },
};

  console.log("📤 Submitting Announcement:", payload);

  try {
    if (isEditing) {
      await axios.put(`${API_BASE}/api/admin/announcements/${editingId}`, payload, config);
      alert("✅ Announcement updated successfully");
    } else {
      await axios.post(`${API_BASE}/api/admin/announcements`, payload, config);
      alert("✅ Announcement created successfully");
    }

    setForm(initialForm);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(false);
    fetchAnnouncements();
  } catch (err) {
    console.error("❌ Failed to save announcement:", err.response?.data || err.message);
    alert(`Error saving announcement: ${err.response?.data?.message || err.message}`);
  } finally {
    setLoading(false);
  }
};

  const handleEdit = (announcement) => {
    setForm({
      title: announcement.title,
      body: announcement.body,
      category: announcement.category,
      isImportant: announcement.isImportant,
      visibility: {
        startDate: announcement.visibility.startDate.split("T")[0],
        endDate: announcement.visibility.endDate.split("T")[0],
      },
    });
    setIsEditing(true);
    setEditingId(announcement._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAnnouncements();
    } catch (err) {
      console.error("Failed to delete announcement", err);
    }
  };

  const filteredAnnouncements = Array.isArray(announcements)
  ? filteredCategory === "All"
    ? announcements
    : announcements.filter((a) => a.category === filteredCategory)
  : [];

  return (
    <div className="p-6 text-gray-900 dark:text-gray-100">
      <h2 className="text-2xl font-bold mb-4">📢 Announcements Manager</h2>

      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <label className="font-medium">Filter by Category:</label>
          <select
            className="border px-2 py-1 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            value={filteredCategory}
            onChange={(e) => setFilteredCategory(e.target.value)}
          >
            <option value="All">All</option>
            {categories.map((cat) => (
              <option key={cat}>{cat}</option>
            ))}
          </select>
        </div>
        <button
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600"
          onClick={() => {
            setForm(initialForm);
            setShowForm(true);
            setIsEditing(false);
            setEditingId(null);
          }}
        >
          + Post New Announcement
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded shadow border dark:border-gray-700">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="px-3 py-2 border dark:border-gray-600 text-left">Title</th>
              <th className="px-3 py-2 border dark:border-gray-600 text-left">Category</th>
              <th className="px-3 py-2 border dark:border-gray-600 text-center">Important</th>
              <th className="px-3 py-2 border dark:border-gray-600 text-center">Visible From</th>
              <th className="px-3 py-2 border dark:border-gray-600 text-center">Until</th>
              <th className="px-3 py-2 border dark:border-gray-600 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAnnouncements.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No announcements found.
                </td>
              </tr>
            )}
            {filteredAnnouncements.map((a) => (
              <tr key={a._id} className="text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-3 py-2 border dark:border-gray-600">{a.title}</td>
                <td className="px-3 py-2 border dark:border-gray-600">{a.category}</td>
                <td className="px-3 py-2 border dark:border-gray-600 text-center">
                  {a.isImportant ? "✅" : "❌"}
                </td>
                <td className="px-3 py-2 border dark:border-gray-600 text-center">
                  {format(new Date(a.visibility.startDate), "yyyy-MM-dd")}
                </td>
                <td className="px-3 py-2 border dark:border-gray-600 text-center">
                  {format(new Date(a.visibility.endDate), "yyyy-MM-dd")}
                </td>
                <td className="px-3 py-2 border dark:border-gray-600 text-center space-x-2">
                  <button
                    onClick={() => handleEdit(a)}
                    className="px-2 py-1 text-blue-600 hover:underline dark:text-blue-400"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(a._id)}
                    className="px-2 py-1 text-red-600 hover:underline dark:text-red-400"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 w-full max-w-lg p-6 rounded shadow-lg relative">
            <h3 className="text-xl font-bold mb-4">
              {isEditing ? "Edit Announcement" : "Post New Announcement"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                name="title"
                placeholder="Title"
                className="w-full border p-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                value={form.title}
                onChange={handleInputChange}
                required
              />
              <textarea
                name="body"
                placeholder="Body / Message"
                className="w-full border p-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                value={form.body}
                onChange={handleInputChange}
                required
              />
              <div className="flex gap-4 flex-wrap">
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-sm">Category</label>
                  <select
                    name="category"
                    className="w-full border p-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    value={form.category}
                    onChange={handleInputChange}
                  >
                    {categories.map((cat) => (
                      <option key={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 flex items-end min-w-[150px]">
                  <label className="flex items-center space-x-2 mt-5">
                    <input
                      type="checkbox"
                      name="isImportant"
                      checked={form.isImportant}
                      onChange={handleInputChange}
                      className="accent-blue-500 dark:accent-blue-400"
                    />
                    <span>Important</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-4 flex-wrap">
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-sm">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    className="w-full border p-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    value={form.visibility.startDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-sm">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    className="w-full border p-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    value={form.visibility.endDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setForm(initialForm);
                  }}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  {loading ? (isEditing ? "Updating..." : "Posting...") : isEditing ? "Update" : "Post"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementManager;
